// Pure data-shaping and rendering for the Agents page: the registered agents
// laid out as a list of rows, each with enable/disable, edit, and remove
// actions, plus an always-present add form. Mirrors the board-columns.ts seam
// — a render function that takes an injected api so no Connection, routing, or
// network of its own lives here. Agents live on the daemon and are reached
// through the real action-style Fabrika endpoints (there is no PATCH route).
// Field set mirrors fabrika/web/src/views/agents.ts so the phone form matches
// the desktop form exactly (same kind/model pickers, roles, tags, priority,
// photo) — the two UIs share one Agent shape on the daemon.
import { el } from "../dom.js";
import { ROLES, type Agent } from "../types.js";
import { AGENT_KINDS } from "../agentKinds.js";
import { createModelPicker } from "./modelPicker.js";
import { DEFAULT_AVATAR } from "../avatar.js";

export interface AgentsCtx {
  api: <T = unknown>(method: string, path: string, body?: unknown) => Promise<T>;
  onChanged: () => void;
}

const kindFor = (command: string) => AGENT_KINDS.find((k) => k.command === command);

// Routing priority levels. Higher number = higher routing priority; 0 = normal.
const PRIORITY_OPTIONS = [
  { value: -1, label: "Low" },
  { value: 0, label: "Normal" },
  { value: 1, label: "High" },
];
const priorityLabel = (p: number) => PRIORITY_OPTIONS.find((o) => o.value === p)?.label ?? "Normal";

// Split a comma-separated input (tags) into trimmed, non-empty entries.
function parseList(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// A labelled form control: a stacked caption + control, sharing the app's
// .field rhythm so add/edit forms line up. A plain div (not <label>) since
// some controls (the roles checkbox row) wrap more than one input.
function field(label: string, control: HTMLElement): HTMLElement {
  return el("div", { class: "field" }, [el("span", {}, [label]), control]);
}

// The kind/roles/model/tags/priority summary under an agent's name (empty
// parts dropped).
function metaLine(agent: Agent): string {
  const parts = [kindFor(agent.command)?.label ?? agent.command, agent.roles.join(", "), agent.model].filter(
    (p) => p.length > 0,
  );
  const tail: string[] = [];
  if (agent.tags.length > 0) tail.push(agent.tags.join(", "));
  if ((agent.priority ?? 0) !== 0) tail.push(`priority: ${priorityLabel(agent.priority ?? 0)}`);
  return [...parts, ...tail].join("  ·  ") || "—";
}

export function renderAgents(agents: Agent[], ctx: AgentsCtx): HTMLElement {
  const root = el("div", { class: "agents-list" });

  if (agents.length === 0) {
    root.append(el("div", { class: "empty" }, ["No agents yet."]));
  }

  for (const agent of agents) {
    root.append(agentRow(agent, ctx));
  }

  root.append(addForm(ctx));
  return root;
}

function agentRow(agent: Agent, ctx: AgentsCtx): HTMLElement {
  const row = el("div", { class: "agent-row", "data-agent-id": agent.id });

  const toggle = el(
    "button",
    {
      class: "agent-toggle",
      onclick: async () => {
        const path = agent.enabled
          ? `/api/agents/${agent.id}/disable`
          : `/api/agents/${agent.id}/enable`;
        await ctx.api("POST", path);
        ctx.onChanged();
      },
    },
    [agent.enabled ? "Disable" : "Enable"],
  );

  const edit = el(
    "button",
    {
      class: "agent-edit",
      onclick: () => {
        if (row.querySelector(".agent-edit-form")) return;
        row.append(editForm(agent, ctx));
      },
    },
    ["Edit"],
  );

  const remove = el(
    "button",
    {
      class: "agent-delete danger",
      onclick: async () => {
        if (!confirm(`Remove agent "${agent.name}"?`)) return;
        await ctx.api("DELETE", `/api/agents/${agent.id}`);
        ctx.onChanged();
      },
    },
    ["Remove"],
  );

  row.append(
    el("div", { class: "agent-head" }, [
      el("div", { class: "agent-identity" }, [
        el("img", { class: "agent-avatar", src: agent.photo || DEFAULT_AVATAR, alt: "" }),
        el("span", { class: "name" }, [agent.name]),
      ]),
      el("span", { class: `agent-state ${agent.enabled ? "on" : "off"}` }, [
        agent.enabled ? "enabled" : "disabled",
      ]),
    ]),
    el("div", { class: "agent-meta" }, [metaLine(agent)]),
    el("div", { class: "actions" }, [toggle, edit, remove]),
  );
  return row;
}

// Builds the shared field set for both the add and edit forms — name, agent
// kind (which drives the command template), model, tags, concurrency,
// timeout, max attempts, priority, roles, and photo — so the two forms stay
// in lockstep and match the desktop form field-for-field.
function buildAgentFields(agent?: Agent) {
  const nameInput = el("input", { value: agent?.name ?? "", placeholder: "e.g. Bumblebee" }) as HTMLInputElement;

  const kindSelect = el(
    "select",
    {},
    AGENT_KINDS.map((k) => el("option", { value: k.id }, [k.label])),
  ) as HTMLSelectElement;
  const picker = createModelPicker();
  const populateModels = (kindId: string, selected?: string) => {
    const k = AGENT_KINDS.find((x) => x.id === kindId) ?? AGENT_KINDS[0];
    picker.setModels(k.models, selected);
  };
  kindSelect.addEventListener("change", () => populateModels(kindSelect.value));
  kindSelect.value = (agent ? kindFor(agent.command) : undefined)?.id ?? AGENT_KINDS[0].id;
  populateModels(kindSelect.value, agent?.model);

  const tagsInput = el("input", { value: agent?.tags.join(", ") ?? "", placeholder: "go, frontend" }) as HTMLInputElement;
  const concurrencyInput = el("input", {
    type: "number",
    min: "1",
    value: String(agent?.concurrency ?? 1),
  }) as HTMLInputElement;
  const timeoutInput = el("input", { value: agent?.timeout ?? "20m", placeholder: "20m" }) as HTMLInputElement;
  const maxAttemptsInput = el("input", {
    type: "number",
    min: "1",
    value: String(agent?.maxAttempts ?? 3),
  }) as HTMLInputElement;

  const prioritySelect = el(
    "select",
    {},
    PRIORITY_OPTIONS.map((p) => el("option", { value: String(p.value) }, [p.label])),
  ) as HTMLSelectElement;
  prioritySelect.value = String(agent?.priority ?? 0);

  const roleBoxes = ROLES.map((r) => {
    const cb = el("input", { type: "checkbox" }) as HTMLInputElement;
    cb.checked = agent ? agent.roles.includes(r) : r === "implementer";
    return { role: r, cb };
  });

  const errSlot = el("div", {});
  const photoPreview = el("img", { class: "agent-avatar-preview", src: agent?.photo || DEFAULT_AVATAR, alt: "" }) as HTMLImageElement;
  const photoHint = el("span", { class: "meta" }, [agent?.photo ? "Current photo." : "Optional — default avatar otherwise."]);
  const photoInput = el("input", { type: "file", accept: "image/*", class: "hidden-file" }) as HTMLInputElement;
  const photoBtn = el("button", { type: "button", onclick: () => photoInput.click() }, ["Choose photo…"]);
  let photoDataUrl = agent?.photo || "";

  photoInput.addEventListener("change", () => {
    errSlot.replaceChildren();
    const file = photoInput.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      errSlot.replaceChildren(el("div", { class: "error-banner" }, ["File must be an image."]));
      photoInput.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      errSlot.replaceChildren(el("div", { class: "error-banner" }, ["Image must be under 2 MiB."]));
      photoInput.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      photoDataUrl = reader.result as string;
      photoPreview.src = photoDataUrl;
      photoHint.textContent = file.name;
    };
    reader.readAsDataURL(file);
  });

  const nodes = [
    field("Name", nameInput),
    field("Agent", kindSelect),
    field("Model", picker.el),
    field("Tags", tagsInput),
    el("div", { class: "field-group" }, [
      field("Concurrency", concurrencyInput),
      field("Timeout", timeoutInput),
      field("Max attempts", maxAttemptsInput),
      field("Priority", prioritySelect),
    ]),
    field(
      "Roles",
      el(
        "div",
        { class: "role-row" },
        roleBoxes.map((b) => el("label", { class: "meta role-check" }, [b.cb, b.role])),
      ),
    ),
    field("Photo", el("div", { class: "agent-photo-row" }, [photoPreview, photoBtn, photoHint, photoInput])),
    errSlot,
  ];

  const collect = (): Omit<Agent, "id" | "enabled"> => {
    const selectedKind = AGENT_KINDS.find((k) => k.id === kindSelect.value) ?? AGENT_KINDS[0];
    return {
      name: nameInput.value.trim() || selectedKind.label,
      command: selectedKind.command,
      model: picker.getValue(),
      roles: roleBoxes.filter((b) => b.cb.checked).map((b) => b.role),
      tags: parseList(tagsInput.value),
      concurrency: Number(concurrencyInput.value) || 1,
      timeout: timeoutInput.value.trim(),
      maxAttempts: Number(maxAttemptsInput.value) || 1,
      priority: Number(prioritySelect.value) || 0,
      photo: photoDataUrl,
    };
  };

  return { nodes, collect };
}

function editForm(agent: Agent, ctx: AgentsCtx): HTMLElement {
  const fields = buildAgentFields(agent);

  const form = el("div", { class: "agent-edit-form agent-form" }, [
    el("div", { class: "agent-form-title" }, ["Edit agent"]),
    ...fields.nodes,
  ]);

  const save = el(
    "button",
    {
      class: "agent-save primary",
      onclick: async () => {
        const updated: Agent = { ...agent, ...fields.collect() };
        await ctx.api("PUT", `/api/agents/${agent.id}`, updated);
        ctx.onChanged();
      },
    },
    ["Save"],
  );

  const cancel = el(
    "button",
    { class: "agent-cancel", onclick: () => form.remove() },
    ["Cancel"],
  );

  form.append(el("div", { class: "actions" }, [save, cancel]));
  return form;
}

function addForm(ctx: AgentsCtx): HTMLElement {
  const fields = buildAgentFields();

  const add = el(
    "button",
    {
      class: "agent-add primary",
      onclick: async () => {
        const agent: Agent = { id: "", enabled: true, ...fields.collect() };
        await ctx.api("POST", "/api/agents", agent);
        ctx.onChanged();
      },
    },
    ["Add agent"],
  );

  return el("div", { class: "agent-add-form agent-form" }, [
    el("div", { class: "agent-form-title" }, ["New agent"]),
    ...fields.nodes,
    el("div", { class: "actions" }, [add]),
  ]);
}
